/* eslint-disable max-lines */
import { HttpStatusCode } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Action } from "@ngrx/store";
import { catchError, map, mergeMap, Observable, of, switchMap } from "rxjs";

import { Complaint4D8DService, Complaint8DNameSlimModel, ComplaintChartsService, ComplaintEightDNamesService, ComplaintErrorCodeModel, ComplaintErrorCodesService, ComplaintPartAnalysesService, ComplaintPartGroupsService, ComplaintsService, ComplaintSupplierComplaintNumbersService, ComplaintSupplierPartAnalysesService, DataPartAnalysisResponse, DefectivePartAnalysisSupplierResponseModel } from "../../../shared/generated";
import { PartsActions } from "./parts.actions";

@Injectable()
export class PartsEffects {
  private readonly complaintSupplierPartAnalysesService = inject(ComplaintSupplierPartAnalysesService);
  private readonly complaintSupplierComplaintNumbersService = inject(ComplaintSupplierComplaintNumbersService);
  private readonly complaintPartGroupsService = inject(ComplaintPartGroupsService);
  private readonly complaintPartAnalysesService = inject(ComplaintPartAnalysesService);
  private readonly complaintErrorCodesService = inject(ComplaintErrorCodesService);
  private readonly complaintEightDNamesService = inject(ComplaintEightDNamesService);
  private readonly complaint4D8DService = inject(Complaint4D8DService);
  private readonly complaintsService = inject(ComplaintsService);
  private readonly complaintChartsService = inject(ComplaintChartsService);
  private readonly _router = inject(Router);

  private readonly actions$: Actions = inject(Actions);

  getAllComplaints$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.getAllComplaints),
      switchMap((action) =>
        this.complaintsService
          .apiComplaintsGet(
            action.sources,
            action.partGroups,
            action.supplierNumbers,
            action.supplierDecisions,
            action.statusPartAnalysis,
            action.statusClientResponses,
            action.warrantyCenters,
            action.supplierComplaintNumbers,
            action.shipmentNumber,
            action.partIdentNumber,
            action.defectivePartNumber,
            action.shipmentDateFrom,
            action.shipmentDateTo,
            action.index,
            action.size,
            action.sortColumn,
            action.sortDirectionType,
            'body',
            false
          )
          .pipe(
            map((complaintsPayload: any) =>
              PartsActions.getAllComplaintsSuccess({
                complaints: complaintsPayload,
              }),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.getAllComplaintsError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  getAllComplaintsByAdmin$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.getAllComplaintsByAdmin),
      switchMap((action) =>
        this.complaintsService
          .apiAdminComplaintsGet(
            action.sources,
            action.partGroups,
            action.supplierNumbers,
            action.supplierDecisions,
            action.statusPartAnalysis,
            action.statusClientResponses,
            action.warrantyCenters,
            action.supplierComplaintNumbers,
            action.shipmentNumber,
            action.partIdentNumber,
            action.defectivePartNumber,
            action.shipmentDateFrom,
            action.shipmentDateTo,
            action.index,
            action.size,
            action.sortColumn,
            action.sortDirectionType,
            'body',
            false
          )
          .pipe(
            map((complaintsPayload: any) =>
              PartsActions.getAllComplaintsByAdminSuccess({
                complaints: complaintsPayload,
              }),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.getAllComplaintsByAdminError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  createComplaint$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.createComplaint),
      switchMap((payload) =>
        this.complaintsService
          .apiComplaintsPost(payload.addComplaintModel)
          .pipe(
            map(() =>
              PartsActions.createComplaintSuccess(),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.createComplaintError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  getEightDNames$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.getAll8dNames),
      switchMap((action) =>
        this.complaintEightDNamesService
          .apiComplaint8dNamesGet(
            action.index,
            action.size,
            action.sources,
            action.name,
            action.clientProblemCode8D,
            action.supplierProblemCode8D,
            action.sortColumn,
            action.sortDirectionType,
            'body',
            false
          )
          .pipe(
            map((eightDNamesPayload: any) =>
              PartsActions.getAll8dNamesSuccess({
                eightDNames: eightDNamesPayload,
              }),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.getAll8dNamesError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  getAllPartGroups$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.getAllPartGroups),
      switchMap((action) =>
        this.complaintPartGroupsService
          .apiComplaintPartGroupsGet(
            action.sources,
            action.partGroup,
            action.clientPartNumber,
            action.warrantyCenter,
            action.supplierPartNumber,
            action.index,
            action.size,
            action.sortColumn,
            action.sortDirectionType,
            'body',
            false
          )
          .pipe(
            map((partGroupsPayload: any) =>
              PartsActions.getAllPartGroupsSuccess({
                partGroups: partGroupsPayload,
              }),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.getAllPartGroupsError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  getAllSupplierComplaintNumbers$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.getAllSupplierComplaintNumbers),
      switchMap((action) =>
        this.complaintSupplierComplaintNumbersService
          .apiComplaintSupplierComplaintNumbersGet(
            action.sources,
            action.partIdentNumber,
            action.supplierComplaintNumber,
            action.shipmentNumber,
            action.index,
            action.size,
            action.sortColumn,
            action.sortDirectionType,
            'body',
            false
          )
          .pipe(
            map((payload: any) =>
              PartsActions.getAllSupplierComplaintNumbersSuccess({
                complaintSupplierComplaintNumbers: payload,
              }),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.getAllSupplierComplaintNumbersError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  getAllErrorCodes$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.getAllErrorCodes),
      switchMap((action) =>
        this.complaintErrorCodesService
          .apiComplaintErrorCodesGet(
            action.sources,
            action.errorCode,
            action.errorCodeName,
            action.index,
            action.size,
            action.sortColumn,
            action.sortDirectionType,
            'body',
            false
          )
          .pipe(
            map((errorCodesPayload: any) =>
              PartsActions.getAllErrorCodesSuccess({
                errorCodes: errorCodesPayload,
              }),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.getAllErrorCodesError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  put8DName$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.put8DName),
      switchMap((action) =>
        this.complaintEightDNamesService
          .apiComplaint8dNamesIdPut(
            action.id,
            action.updateComplaint8DNameRequest
          )
          .pipe(
            map(() =>
              PartsActions.put8DNameSuccess(),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.put8DNameError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  putPartGroup$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.putPartGroup),
      switchMap((action) =>
        this.complaintPartGroupsService
          .apiComplaintPartGroupsIdPut(
            action.id,
            action.complaintPartGroupRequest
          )
          .pipe(
            map(() =>
              PartsActions.putPartGroupSuccess(),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.putPartGroupError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  putComplaintSupplierComplaintNumber$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.putComplaintSupplierComplaintNumber),
      switchMap((action) =>
        this.complaintSupplierComplaintNumbersService
          .apiComplaintSupplierComplaintNumbersIdPut(
            action.id,
            action.complaintSupplierComplaintNumberRequest
          )
          .pipe(
            map(() =>
              PartsActions.putComplaintSupplierComplaintNumberSuccess(),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.putComplaintSupplierComplaintNumberError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  putErrorCode$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.putErrorCode),
      switchMap((action) =>
        this.complaintErrorCodesService
          .apiComplaintErrorCodesIdPut(
            action.id,
            action.errorCode,
            action.source,
            action.errorLabelGerman,
            action.errorDescriptionGerman,
            action.errorLabelEnglish,
            action.errorDescriptionEnglish,
            action.errorCodeName,
            action.isActive
          )
          .pipe(
            map(() =>
              PartsActions.putErrorCodeSuccess(),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.putErrorCodeError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  getDownloadComplaintsAsCsv$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.getDownloadComplaintsAsCsv),
      switchMap((action) =>
        this.complaintsService
          .apiComplaintsExportCsvGet(
            action.sources,
            action.partGroups,
            action.supplierNumbers,
            action.supplierDecisions,
            action.statusPartAnalysis,
            action.statusClientResponses,
            action.warrantyCenters,
            action.supplierComplaintNumbers,
            action.shipmentNumber,
            action.partIdentNumber,
            action.defectivePartNumber,
            action.shipmentDateFrom,
            action.shipmentDateTo,
            action.sortColumn,
            action.sortDirectionType,
            'body',
            false,
            { httpHeaderAccept: 'text/plain' }
          )
          .pipe(
            map((payload: any) =>
              PartsActions.getDownloadComplaintsAsCsvSuccess({
                csvFile: payload,
              }),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.getDownloadComplaintsAsCsvError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  getDownloadComplaintsAsExcel$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.getDownloadComplaintsAsExcel),
      switchMap((action) =>
        this.complaintsService
          .apiComplaintsExportExcelGet(
            action.sources,
            action.partGroups,
            action.supplierNumbers,
            action.supplierDecisions,
            action.statusPartAnalysis,
            action.statusClientResponses,
            action.warrantyCenters,
            action.supplierComplaintNumbers,
            action.shipmentNumber,
            action.partIdentNumber,
            action.defectivePartNumber,
            action.shipmentDateFrom,
            action.shipmentDateTo,
            action.sortColumn,
            action.sortDirectionType,
            'body',
            false,
            { httpHeaderAccept: 'text/plain' }
          )
          .pipe(
            map((payload: any) =>
              PartsActions.getDownloadComplaintsAsExcelSuccess({
                excelFile: payload,
              }),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.getDownloadComplaintsAsExcelError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  getAllClaimTableFilterOptions$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.getAllComplaintTableFilterOptions),
      switchMap(() =>
        this.complaintsService
          .apiComplaintsFilterOptionsGet()
          .pipe(
            map((complaintTableFilterOptionsPayload: any) =>
              PartsActions.getAllComplaintTableFilterOptionsSuccess({
                complaintTableFilterOptions: complaintTableFilterOptionsPayload,
              }),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.getAllComplaintTableFilterOptionsError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  getAllSources$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.getAllSources),
      switchMap(() =>
        this.complaintsService
          .apiComplaintsLookupsSourcesGet()
          .pipe(
            map((sourcesPayload: any) =>
              PartsActions.getAllSourcesSuccess({
                sources: sourcesPayload,
              }),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.getAllSourcesError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  getAllComplaintChartFilterOptions$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.getAllComplaintChartFilterOptions),
      switchMap(() =>
        this.complaintChartsService
          .apiComplaintChartsFilterOptionsGet()
          .pipe(
            map((complaintChartFilterOptionsPayload: any) =>
              PartsActions.getAllComplaintChartFilterOptionsSuccess({
                complaintChartFilterOptions: complaintChartFilterOptionsPayload,
              }),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.getAllComplaintChartFilterOptionsError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  readDataFromPorschePdf$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.postReadDataFromPorschePdf),
      switchMap((payload) =>
        this.complaintsService
          .apiComplaintsImportPorschePdfPost(payload.file)
          .pipe(
            map(() =>
              PartsActions.postReadDataFromPorschePdfSuccess(),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.postReadDataFromPorschePdfError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));


  deleteComplaint$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.deleteComplaint),
      switchMap((payload) =>
        this.complaintsService
          .apiComplaintsIdDelete(payload.complainId, payload.hashValue)
          .pipe(
            map(() =>
              PartsActions.deleteComplaintSuccess(),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.deleteComplaintError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  restoreComplaint$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.restoreComplaint),
      switchMap((payload) =>
        this.complaintsService
          .apiComplaintsIdRestorePost(payload.complainId)
          .pipe(
            map(() =>
              PartsActions.restoreComplaintSuccess(),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.restoreComplaintError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  getJsonForPartIdentNumber$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.getJsonForPartIdentNumber),
      switchMap((payload) =>
        this.complaintsService
          .apiComplaintsPartIdentNumberJsonGet(payload.partIdentNumber)
          .pipe(
            map((complaintsDetailsPayload) =>
              PartsActions.getJsonForPartIdentNumberSuccess({
                complaintsDetails: complaintsDetailsPayload
              }),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.getJsonForPartIdentNumberError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  getChartCustomerDecisionPerShipmentYear$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.getChartCustomerDecisionPerShipmentYear),
      switchMap((payload) =>
        this.complaintChartsService
          .apiComplaintChartsCustomerDecisionPerShipmentYearGet(
            payload.shipmentDateFrom,
            payload.shipmentDateTo,
            payload.sources,
            payload.supplierNumbers,
            payload.partGroups,
            payload.returnCountries
          )
          .pipe(
            map((payload) =>
              PartsActions.getChartCustomerDecisionPerShipmentYearSuccess(
                { chartData: payload }
              ),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.getChartCustomerDecisionPerShipmentYearError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  getChartCustomerDecisionPerShipmentMonth$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.getChartCustomerDecisionPerShipmentMonth),
      switchMap((payload) =>
        this.complaintChartsService
          .apiComplaintChartsCustomerDecisionPerShipmentMonthGet(
            payload.shipmentDateFrom,
            payload.shipmentDateTo,
            payload.sources,
            payload.supplierNumbers,
            payload.partGroups,
            payload.returnCountries
          )
          .pipe(
            map((payload) =>
              PartsActions.getChartCustomerDecisionPerShipmentMonthSuccess(
                { chartData: payload }
              ),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.getChartCustomerDecisionPerShipmentMonthError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  getChartSupplierFeedbackPerShipmentYear$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.getChartSupplierFeedbackPerShipmentYear),
      switchMap((payload) =>
        this.complaintChartsService
          .apiComplaintChartsSupplierFeedbackPerShipmentYearGet(
            payload.shipmentDateFrom,
            payload.shipmentDateTo,
            payload.sources,
            payload.supplierNumbers,
            payload.partGroups,
            payload.returnCountries
          )
          .pipe(
            map((payload) =>
              PartsActions.getChartSupplierFeedbackPerShipmentYearSuccess(
                { chartData: payload }
              ),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.getChartSupplierFeedbackPerShipmentYearError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  getChartSupplierFeedbackPerShipmentMonth$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.getChartSupplierFeedbackPerShipmentMonth),
      switchMap((payload) =>
        this.complaintChartsService
          .apiComplaintChartsSupplierFeedbackPerShipmentMonthGet(
            payload.shipmentDateFrom,
            payload.shipmentDateTo,
            payload.sources,
            payload.supplierNumbers,
            payload.partGroups,
            payload.returnCountries
          )
          .pipe(
            map((payload) =>
              PartsActions.getChartSupplierFeedbackPerShipmentMonthSuccess(
                { chartData: payload }
              ),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.getChartSupplierFeedbackPerShipmentMonthError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  getChartSupplierFeedbackPerRepairYear$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.getChartSupplierFeedbackPerRepairYear),
      switchMap((payload) =>
        this.complaintChartsService
          .apiComplaintChartsSupplierFeedbackPerRepairYearGet(
            payload.repairDateFrom,
            payload.repairDateTo,
            payload.sources,
            payload.supplierNumbers,
            payload.partGroups,
            payload.returnCountries
          )
          .pipe(
            map((payload) =>
              PartsActions.getChartSupplierFeedbackPerRepairYearSuccess(
                { chartData: payload }
              ),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.getChartSupplierFeedbackPerRepairYearError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  getChartSupplierFeedbackPerRepairMonth$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.getChartSupplierFeedbackPerRepairMonth),
      switchMap((payload) =>
        this.complaintChartsService
          .apiComplaintChartsSupplierFeedbackPerRepairMonthGet(
            payload.repairDateFrom,
            payload.repairDateTo,
            payload.sources,
            payload.supplierNumbers,
            payload.partGroups,
            payload.returnCountries
          )
          .pipe(
            map((payload) =>
              PartsActions.getChartSupplierFeedbackPerRepairMonthSuccess(
                { chartData: payload }
              ),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.getChartSupplierFeedbackPerRepairMonthError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  getChartSupplierFeedbackPerPartProductionYear$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.getChartSupplierFeedbackPerPartProductionYear),
      switchMap((payload) =>
        this.complaintChartsService
          .apiComplaintChartsSupplierFeedbackPerPartProductionYearGet(
            payload.partProductionDateFrom,
            payload.partProductionDateTo,
            payload.sources,
            payload.supplierNumbers,
            payload.partGroups,
            payload.returnCountries
          )
          .pipe(
            map((payload) =>
              PartsActions.getChartSupplierFeedbackPerPartProductionYearSuccess(
                { chartData: payload }
              ),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.getChartSupplierFeedbackPerPartProductionYearError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  getChartSupplierFeedbackPerPartProductionMonth$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.getChartSupplierFeedbackPerPartProductionMonth),
      switchMap((payload) =>
        this.complaintChartsService
          .apiComplaintChartsSupplierFeedbackPerPartProductionMonthGet(
            payload.partProductionDateFrom,
            payload.partProductionDateTo,
            payload.sources,
            payload.supplierNumbers,
            payload.partGroups,
            payload.returnCountries
          )
          .pipe(
            map((payload) =>
              PartsActions.getChartSupplierFeedbackPerPartProductionMonthSuccess(
                { chartData: payload }
              ),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.getChartSupplierFeedbackPerPartProductionMonthError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  getChartCustomerDecisionPerPartProductionYear$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.getChartCustomerDecisionPerPartProductionYear),
      switchMap((payload) =>
        this.complaintChartsService
          .apiComplaintChartsCustomerDecisionPerPartProductionYearGet(
            payload.partProductionDateFrom,
            payload.partProductionDateTo,
            payload.sources,
            payload.supplierNumbers,
            payload.partGroups,
            payload.returnCountries
          )
          .pipe(
            map((payload) =>
              PartsActions.getChartCustomerDecisionPerPartProductionYearSuccess(
                { chartData: payload }
              ),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.getChartCustomerDecisionPerPartProductionYearError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  getChartCustomerDecisionPerPartProductionMonth$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.getChartCustomerDecisionPerPartProductionMonth),
      switchMap((payload) =>
        this.complaintChartsService
          .apiComplaintChartsCustomerDecisionPerPartProductionMonthGet(
            payload.partProductionDateFrom,
            payload.partProductionDateTo,
            payload.sources,
            payload.supplierNumbers,
            payload.partGroups,
            payload.returnCountries
          )
          .pipe(
            map((payload) =>
              PartsActions.getChartCustomerDecisionPerPartProductionMonthSuccess(
                { chartData: payload }
              ),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.getChartCustomerDecisionPerPartProductionMonthError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  getChartEightDNamePerPartProductionMonth$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.getChartEightDNamePerPartProductionMonth),
      switchMap((payload) =>
        this.complaintChartsService
          .apiComplaintCharts8dNamePerPartProductionMonthGet(
            payload.partProductionDateFrom,
            payload.partProductionDateTo,
            payload.sources,
            payload.supplierNumbers,
            payload.partGroups,
            payload.returnCountries
          )
          .pipe(
            map((payload) =>
              PartsActions.getChartEightDNamePerPartProductionMonthSuccess(
                { chartData: payload }
              ),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.getChartEightDNamePerPartProductionMonthError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  getChartPartProductionYearPerSupplierNumber$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.getChartPartProductionYearPerSupplierNumber),
      switchMap((payload) =>
        this.complaintChartsService
          .apiComplaintChartsPartProductionYearPerSupplierNumberGet(
            payload.partProductionDateFrom,
            payload.partProductionDateTo,
            payload.sources,
            payload.supplierNumbers,
            payload.partGroups,
            payload.returnCountries
          )
          .pipe(
            map((payload) =>
              PartsActions.getChartPartProductionYearPerSupplierNumberSuccess(
                { chartData: payload }
              ),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.getChartPartProductionYearPerSupplierNumberError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  getChartRepairCountryPerSupplierNumber$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.getChartRepairCountryPerSupplierNumber),
      switchMap((payload) =>
        this.complaintChartsService
          .apiComplaintChartsRepairCountryPerSupplierNumberGet(
            payload.shipmentDateFrom,
            payload.shipmentDateTo,
            payload.sources,
            payload.supplierNumbers,
            payload.partGroups,
            payload.returnCountries
          )
          .pipe(
            map((payload) =>
              PartsActions.getChartRepairCountryPerSupplierNumberSuccess(
                { chartData: payload }
              ),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.getChartRepairCountryPerSupplierNumberError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  getChartCustomerDecisionPerVehicleModel$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.getChartCustomerDecisionPerVehicleModel),
      switchMap((payload) =>
        this.complaintChartsService
          .apiComplaintChartsCustomerDecisionPerVehicleModelGet(
            payload.shipmentDateFrom,
            payload.shipmentDateTo,
            payload.sources,
            payload.supplierNumbers,
            payload.partGroups,
            payload.returnCountries
          )
          .pipe(
            map((payload) =>
              PartsActions.getChartCustomerDecisionPerVehicleModelSuccess(
                { chartData: payload }
              ),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.getChartCustomerDecisionPerVehicleModelError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  getChartCustomerDecisionPerReturnCountry$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.getChartCustomerDecisionPerReturnCountry),
      switchMap((payload) =>
        this.complaintChartsService
          .apiComplaintChartsCustomerDecisionPerReturnCountryGet(
            payload.shipmentDateFrom,
            payload.shipmentDateTo,
            payload.sources,
            payload.supplierNumbers,
            payload.partGroups,
            payload.returnCountries
          )
          .pipe(
            map((payload) =>
              PartsActions.getChartCustomerDecisionPerReturnCountrySuccess(
                { chartData: payload }
              ),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.getChartCustomerDecisionPerReturnCountryError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  getAllComplaintActiveErrorCodes$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.getAllComplaintActiveErrorCodes),
      switchMap((payload) =>
        this.complaintErrorCodesService
          .apiComplaintErrorCodesActiveGet(payload.source, payload.supplierNumber)
          .pipe(
            map((activeErrorCodesPayload: ComplaintErrorCodeModel[]) =>
              PartsActions.getAllComplaintActiveErrorCodesSuccess({
                activeErrorCodes: activeErrorCodesPayload,
              }),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.getAllComplaintActiveErrorCodesError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  getAllActive8DNamesBySourceAndSupplierNumber$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.getAllActive8DNamesBySourceAndSupplierNumber),
      switchMap((payload) =>
        this.complaintEightDNamesService
          .apiComplaint8dNamesActiveGet(payload.source, payload.supplierNumber)
          .pipe(
            map((rootCausesPayload: Complaint8DNameSlimModel[]) =>
              PartsActions.getAllActive8DNamesBySourceAndSupplierNumberSuccess({
                rootCauses: rootCausesPayload,
              }),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.getAllActive8DNamesBySourceAndSupplierNumberError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  getDefectivePartAnalysisSupplier$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.getDefectivePartAnalysisSupplier),
      switchMap((payload) =>
        this.complaintSupplierPartAnalysesService
          .apiComplaintSupplierPartAnalysesGet(payload.supplierNumber, payload.partIdentNumber)
          .pipe(
            map((defectivePartAnalysisSupplierPayload: DefectivePartAnalysisSupplierResponseModel) =>
              PartsActions.getDefectivePartAnalysisSupplierSuccess({
                defectivePartAnalysisSupplier: defectivePartAnalysisSupplierPayload,
              }),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.getDefectivePartAnalysisSupplierError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  createErrorCode$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.createErrorCode),
      switchMap((payload) =>
        this.complaintErrorCodesService
          .apiComplaintErrorCodesPost(payload.supplierNumber, payload.errorCode, payload.source, payload.errorLabelGerman, payload.errorDescriptionGerman, payload.errorLabelEnglish, payload.errorDescriptionEnglish, payload.errorCodeName, payload.isActive)
          .pipe(
            map(() =>
              PartsActions.createErrorCodeSuccess(),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.createErrorCodeError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  createReferenceReport$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.createReferenceReport),
      switchMap((payload) =>
        this.complaintEightDNamesService
          .apiComplaint8dNamesPost(payload.createComplaint8DNameRequest)
          .pipe(
            map(() =>
              PartsActions.createReferenceReportSuccess(),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.createReferenceReportError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  createDefectivePartAnalysisSupplier$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.createDefectivePartAnalysisSupplier),
      switchMap((payload) =>
        this.complaintSupplierPartAnalysesService
          .apiComplaintSupplierPartAnalysesPost(payload.supplierNumber, payload.partIdentNumber, payload.source, payload.tab, payload.data, payload.files, payload.ntfFiles, payload.transferData, payload.ntfProcess)
          .pipe(
            map((payload: number) =>
              PartsActions.createDefectivePartAnalysisSupplierSuccess({
                defectivePartAnalysisSupplierId: payload,
              }),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.createDefectivePartAnalysisSupplierError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  editDefectivePartAnalysisSupplier$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.editDefectivePartAnalysisSupplier),
      switchMap((payload) =>
        this.complaintSupplierPartAnalysesService
          .apiComplaintSupplierPartAnalysesIdPut(payload.id, payload.supplierNumber, payload.partIdentNumber, payload.source, payload.tab, payload.data, payload.files, payload.ntfFiles, payload.filesForDelete, payload.transferData, payload.ntfProcess)
          .pipe(
            map(() =>
              PartsActions.editDefectivePartAnalysisSupplierSuccess(),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.editDefectivePartAnalysisSupplierError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  downloadFile$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.downloadFile),
      mergeMap((payload) =>
        this.complaintsService
          .apiComplaintsFilesGet(payload.filename, payload.source, payload.partIdentNumber, payload.tab)
          .pipe(
            map((blobPayload: Blob) =>
              PartsActions.downloadFileSuccess({
                file: blobPayload,
                filename: payload.filename as string
              }),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.downloadFileError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  getPartAnalysisData$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.getPartAnalysisData),
      switchMap((payload) =>
        this.complaintPartAnalysesService
          .apiComplaintPartAnalysesGet(payload.supplierNumber, payload.partIdentNumber)
          .pipe(
            map((partAnalysisDataPayload: DataPartAnalysisResponse) =>
              PartsActions.getPartAnalysisDataSuccess({
                partAnalysisData: partAnalysisDataPayload,
              }),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.getPartAnalysisDataError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  editPartAnalysisData$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.editPartAnalysisData),
      switchMap((payload) =>
        this.complaintPartAnalysesService
          .apiComplaintPartAnalysesIdPut(payload.id, payload.source, payload.tab, payload.supplierNumber, payload.partIdentNumber, payload.partAnalysisJsonData, payload.files, payload.filesForDelete)
          .pipe(
            map(() =>
              PartsActions.editPartAnalysisDataSuccess(),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.editPartAnalysisDataError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  savePartAnalysisData$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.savePartAnalysisData),
      switchMap((payload) =>
        this.complaintPartAnalysesService
          .apiComplaintPartAnalysesPost(payload.source, payload.tab, payload.supplierNumber, payload.partIdentNumber, payload.partAnalysisJsonData, payload.files)
          .pipe(
            map(() =>
              PartsActions.savePartAnalysisDataSuccess(),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.savePartAnalysisDataError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  getData4D8D$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.getData4D8D),
      switchMap((action) =>
        this.complaint4D8DService
          .apiComplaint4d8dReportsGet(
            action.supplierNumber,
            action.partIdentNumber,
            'body',
            false
          )
          .pipe(
            map((data4D8DPayload: any) =>
              PartsActions.getData4D8DSuccess({
                data4D8D: data4D8DPayload,
              }),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.getData4D8DError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  postData4D8D$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.postData4D8D),
      switchMap((action) =>
        this.complaint4D8DService
          .apiComplaint4d8dReportsPost(
            action.source,
            action.tab,
            action.supplierNumber,
            action.partIdentNumber,
            action.json4D8DData,
            undefined,
            action.files,
            action.transferData,
            'body',
            false
          )
          .pipe(
            map(() =>
              PartsActions.postData4D8DSuccess(),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.postData4D8DError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  putData4D8D$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.putData4D8D),
      switchMap((action) =>
        this.complaint4D8DService
          .apiComplaint4d8dReportsIdPut(
            action.id,
            action.source,
            action.tab,
            action.supplierNumber,
            action.partIdentNumber,
            action.json4D8DData,
            undefined,
            action.files,
            action.filesForDelete,
            action.transferData,
            'body',
            false
          )
          .pipe(
            map(() =>
              PartsActions.putData4D8DSuccess(),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.putData4D8DError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  getSupplierComplaintNumber$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.getSupplierComplaintNumber),
      switchMap((action) =>
        this.complaintsService
          .apiComplaintsLookupsComplaintNumberGet(
            action.partIdentNumber,
            action.source,
            'body',
            false
          )
          .pipe(
            map((data: any) =>
              PartsActions.getSupplierComplaintNumberSuccess({
                supplierComplaintNumber: data,
              }),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.getSupplierComplaintNumberError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  downloadPorscheFile$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.downloadPorscheFile),
      mergeMap((payload: any) =>
        this.complaintsService
          .apiComplaintsPartIdentNumberPorschePdfGet(payload.partIdentNumber, payload.supplierNumber)
          .pipe(
            map((blobPayload: Blob) =>
              PartsActions.downloadPorscheFileSuccess({
                file: blobPayload,
                supplierNumber: payload.supplierNumber as string
              }),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.downloadPorscheFileError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  getComplaintImportHistory$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.getComplaintImportHistory),
      switchMap((action) =>
        this.complaintsService
          .getComplaintImportHistory(
            action.index,
            action.size,
            action.sortColumn,
            action.sortDirectionType,
            'body',
            false
          )
          .pipe(
            map((complaintImportHistoryPayload: any) =>
              PartsActions.getComplaintImportHistorySuccess({
                complaintImportHistoryResponse: complaintImportHistoryPayload,
              }),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                PartsActions.getComplaintImportHistoryError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  getDiagnosticProtocols$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PartsActions.getDiagnosticProtocols),
      switchMap((action) =>
        this.complaintsService
          .apiComplaintsDiagnosticProtocolsGet(
            action.partIdentNumber,
            action.sourceType,
            'body',
            false
          )
          .pipe(
            map((diagnosticProtocolsPayload: any) =>
              PartsActions.getDiagnosticProtocolsSuccess({
                diagnosticProtocols: diagnosticProtocolsPayload,
              }),
            ),
            catchError((err) => {
              return of(
                PartsActions.getDiagnosticProtocolsError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));
}
